import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchOddsMovements } from '@/api';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { formatOdds, formatPercent, formatRelative } from '@/lib/formatters';
import { MARKET_LABELS } from '@/lib/constants';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowUp, ArrowDown, TrendingUp, ChevronRight } from 'lucide-react';

export default function OddsMovementsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState('');

  useEffect(() => {
    setLoading(true);
    fetchOddsMovements({ direction: direction || undefined, limit: 50 })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [direction]);

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="odds-movements-page">
      <SEOHead title="Odds Movements" description="Track the latest odds changes across all bookmakers. Monitor odds drops and rises in real-time." />

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
        <Link to="/" className="hover:text-slate-300">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300">Odds Movements</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-slate-50 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <TrendingUp className="w-7 h-7 text-blue-400" />
            Odds Movements
          </h1>
          <p className="text-sm text-slate-500 mt-1">Latest odds changes across all bookmakers</p>
        </div>
        <Select value={direction} onValueChange={v => setDirection(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-sm" data-testid="direction-filter">
            <SelectValue placeholder="All Changes" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">All Changes</SelectItem>
            <SelectItem value="up">Odds Up</SelectItem>
            <SelectItem value="down">Odds Down</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingPage /> : (
        <div className="border border-slate-800 rounded-xl overflow-hidden" data-testid="movements-list">
          <div className="bg-slate-900/80 px-4 py-2.5 border-b border-slate-800 grid grid-cols-12 gap-2 text-xs text-slate-500 uppercase tracking-wider font-semibold">
            <div className="col-span-4 md:col-span-3">Match</div>
            <div className="col-span-2 hidden md:block">Market</div>
            <div className="col-span-2">Selection</div>
            <div className="col-span-2">Bookmaker</div>
            <div className="col-span-4 md:col-span-3 text-right">Change</div>
          </div>
          {data?.movements?.map((m, i) => (
            <Link key={i} to={m.match_slug ? `/match/${m.match_slug}` : '#'}
              className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-slate-800/50 hover:bg-slate-900/50 transition-colors text-sm items-center">
              <div className="col-span-4 md:col-span-3 text-slate-300 truncate text-xs">{m.match_name || m.match_id}</div>
              <div className="col-span-2 hidden md:block text-slate-500 text-xs">{MARKET_LABELS[m.market_id] || m.market_id}</div>
              <div className="col-span-2 text-slate-400 text-xs">{m.selection}</div>
              <div className="col-span-2 text-slate-500 text-xs">{m.bookmaker_id}</div>
              <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-2">
                <span className="text-slate-500 tabular-nums text-xs">{formatOdds(m.previous_odd)}</span>
                {m.change_percent > 0
                  ? <ArrowUp className="w-3.5 h-3.5 text-emerald-500" />
                  : <ArrowDown className="w-3.5 h-3.5 text-red-500" />}
                <span className={`tabular-nums font-medium text-sm ${m.change_percent > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatOdds(m.new_odd)}
                </span>
                <Badge className={`text-[10px] px-1.5 ${m.change_percent > 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                  {formatPercent(m.change_percent)}
                </Badge>
              </div>
            </Link>
          ))}
          {!data?.movements?.length && (
            <div className="text-center py-12 text-slate-500">No odds movements found</div>
          )}
        </div>
      )}
    </div>
  );
}
